import { BellFilled, BellOutlined } from "@ant-design/icons"
import { Card, message, Progress, Space, Statistic, Switch, Typography } from "antd"
import Title from "antd/lib/typography/Title"
import { ReactNode } from "react"
import moment from "moment"
import { EventService } from "../../Services/EventService"
import { TextService } from "../../Services/TextService"
import { EventType } from "../../Types/EventType"
import { isOnlyOnWaitlist } from "../../Helpers/WaitlistHelper"
import PageHelp from "../Page/PageHelp"
import { EventStateButton } from "./EventStateButton"
import Paragraph from "antd/lib/typography/Paragraph"

const WAITLIST_ANSWER = 3;
const { Text } = Typography;

/**
 * The top of Status & Svar: the link and an overview of the replies and places. The invites are under Inbjudningar. The name and status
 * are in EventHeader. The event's settings are not repeated here, they are shown and changed under "Ändra event".
 */
export const EventDetails = (props: any) => {
    const GetText = TextService.GetText;
    const isPoll = props.event.eventType === EventType.Poll;
    const times: any[] = props.event.times ?? [];
    const replies: any[] = props.event.timeAnswers ?? [];    // Zero or less means no limit.
    const maxParticipants: number = props.event.eventParticipants ?? 0;

    const replyCount = replies.filter(reply => !isOnlyOnWaitlist(reply)).length;
    const waitlistCount = replies.filter(reply => reply.replyTime?.some((replyTime: any) => replyTime.customerAnswerType === WAITLIST_ANSWER)).length;

    const updateNotificationsForEvent = async (notification: boolean) => {
        await EventService.UpdateNotificationsForEvent(props.event.eventId, notification);
        message.destroy();
        message.success(GetText("EventDetails_ResetColors"));
        props.onNotificationUpdate();
    }

    // A small card with a heading, a big number and an optional line under it.
    const statCard = (key: string, title: ReactNode, value: ReactNode, footer?: ReactNode) =>
        <Card key={key} size="small" style={{ minWidth: 130, flex: "1 1 130px", maxWidth: 260 }}>
            <Statistic title={title} valueRender={() => value} />
            {footer ? <div style={{ marginTop: 4 }}>{footer}</div> : null}
        </Card>;

    // With several times each time gets its own card. Without a limit there is no bar to fill.
    const participantCards = times.map((time: any) => {
        const yesCount: number = time.yesCount ?? 0;
        return statCard("time" + time.id, GetText("EventDetails_StatParticipants") + (times.length > 1 ? " · " + moment(time.start).format("ddd D MMM") : ""),
            maxParticipants > 0 ? <>{yesCount} <Text type="secondary" style={{ fontSize: 16 }}>/ {maxParticipants}</Text></> : yesCount,
            maxParticipants > 0 ?
                <Progress percent={Math.round(yesCount / maxParticipants * 100)} showInfo={false} size="small" status={yesCount >= maxParticipants ? "exception" : "normal"} /> :
                <Text type="secondary">{times.length > 1 ? GetText("EventDetails_NoLimit") : moment(time.start).format("ddd D MMM") + " · " + GetText("EventDetails_NoLimit")}</Text>);
    });

    return <>

        {/* The page's heading like on every page. The name, type and status are in the event's header above the menu. */}
        <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
            <Title style={{ marginBottom: 0 }}>Status & Svar</Title>
            <PageHelp>{GetText("EventDetails_HowItWorks").split("\n\n").map((text, i) => <Paragraph key={i}>{text}</Paragraph>)}</PageHelp>
        </Space>

        {/* The link participants reply through, shown in full so you see what you share, with a copy icon. */}
        {props.event.eventLink ?
            <div style={{ textAlign: "left", marginTop: 4 }}>
                <Text type="secondary">{GetText("EventDetails_EventLink")} </Text>
                <Text copyable={{ text: props.event.eventLink, tooltips: [GetText("EventHeader_CopyLink"), GetText("MyEventDetails_ClipBoard")] }}>
                    <a target="_blank" rel="noreferrer" href={props.event.eventLink}>{props.event.eventLink}</a>
                </Text>
            </div> : null}

        {/* Closes the event, or opens it again, under the link. */}
        <EventStateButton event={props.event} onChanged={props.onClosed} />

        {/* The numbers side by side. A poll has no places, so its replies are counted instead. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 16, marginBottom: 16 }}>
            {isPoll ? statCard("replies", GetText("EventDetails_StatReplies"), replyCount) : participantCards}
            {!isPoll && waitlistCount > 0 ? statCard("waitlist", GetText("EventDetails_StatWaitlist"), waitlistCount) : null}
        </div>

        {/* A setting rather than a status, so it is kept small at the bottom. */}
        <Space size={8}>
            <Switch size="small" checked={props.event.notifyOnReply} onChange={(e) => { updateNotificationsForEvent(!props.event.notifyOnReply) }} checkedChildren={<BellFilled />} unCheckedChildren={<BellOutlined />} />
            <Text type="secondary">{GetText("EventDetails_NotificationToolTip")}</Text>
        </Space>
    </>
}
